import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { LoanType, PoolType, PropertyOccupancyStatus } from '@/types/pool';
import { DEFAULT_COUNTRY_CODE } from '@/utils/consts';
import { UploadedFile } from '../types/s3';
import { calculateUserShare as calcUserShare } from '../utils/poolCalculations';

export enum PoolCreationStep {
  POOL_TYPE = 'POOL_TYPE',
  PERSONAL_INFO = 'PERSONAL_INFO',
  PROPERTY_INFO = 'PROPERTY_INFO',
  POOL_TERMS = 'POOL_TERMS',
  DOCUMENTS = 'DOCUMENTS',
  REVIEW = 'REVIEW',
  SUCCESS = 'SUCCESS',
}

export const POOL_CREATION_STEPPER_STEPS = [
  PoolCreationStep.PERSONAL_INFO,
  PoolCreationStep.PROPERTY_INFO,
  PoolCreationStep.POOL_TERMS,
  PoolCreationStep.DOCUMENTS,
  PoolCreationStep.REVIEW,
];

export const POOL_STEP_CONFIG: Record<PoolCreationStep, { label: string; errorKey: keyof ValidationErrors | null }> = {
  [PoolCreationStep.POOL_TYPE]: { label: 'Pool Type', errorKey: null },
  [PoolCreationStep.PERSONAL_INFO]: { label: 'Personal Info', errorKey: 'personalInfo' },
  [PoolCreationStep.PROPERTY_INFO]: { label: 'Property Info', errorKey: 'propertyInfo' },
  [PoolCreationStep.POOL_TERMS]: { label: 'Pool Terms', errorKey: 'poolTerms' },
  [PoolCreationStep.DOCUMENTS]: { label: 'Documents', errorKey: 'documents' },
  [PoolCreationStep.REVIEW]: { label: 'Review', errorKey: 'review' },
  [PoolCreationStep.SUCCESS]: { label: 'Success', errorKey: null },
};

// Personal Info Types
type PriorName = {
  firstName: string;
  middleName: string;
  lastName: string;
};

export type PersonalInfo = {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
  dateOfBirth: string;
  primaryAddressLine1: string;
  primaryAddressLine2: string;
  primaryCity: string;
  primaryState: string;
  primaryZipCode: string;
  primaryCountry: string;
  priorNames: PriorName[];
  sameAsPrimaryAddress: boolean;
  mailingAddressLine1: string;
  mailingAddressLine2: string;
  mailingCity: string;
  mailingState: string;
  mailingZipCode: string;
  mailingCountry: string;
};

// Property Info Types
type CoOwner = {
  firstName: string;
  middleName: string;
  lastName: string;
  percentage: string;
};

export type ExistingLoan = {
  remainingBalance: string;
};

export type PropertyInfo = {
  propertyAddressLine1: string;
  propertyAddressLine2: string;
  propertyCity: string;
  propertyState: string;
  propertyZipCode: string;
  propertyCountry: string;
  propertyOccupancyStatus: PropertyOccupancyStatus | null;
  propertyValue: string;
  propertyLinks: { url: string }[];
  hasCoOwners: boolean;
  coOwners: CoOwner[];
  existingLoans: ExistingLoan[];
  sameAsPrimaryAddress: boolean;
};

// Pool Terms Types
export type PoolTerms = {
  poolAmount: string;
  roiRate: string;
  loanType: LoanType | null;
  termMonths: string;
};

// Documents Types
export type Documents = {
  governmentId: UploadedFile | null;
  appraisalReport: UploadedFile | null;
  homeInsurance: UploadedFile | null;
  taxReturn: UploadedFile | null;
  mortgageStatement: UploadedFile | null;
  titleDeed: UploadedFile | null;
  coOwnerId: UploadedFile | null;
  propertyPhotos: UploadedFile[];
};

export type OtherDocument = {
  title: string;
  file: UploadedFile | null;
};

// Validation Errors - stored as field-based errors for easy clearing
export type ValidationErrors = {
  personalInfo: Record<string, string[]>;
  propertyInfo: Record<string, string[]>;
  poolTerms: Record<string, string[]>;
  documents: Record<string, string[]>;
  review: Record<string, string[]>;
};

// Store State
type PoolFormState = {
  // Form data
  selectedPoolType: PoolType;
  personalInfo: PersonalInfo;
  propertyInfo: PropertyInfo;
  poolTerms: PoolTerms;
  documents: Documents;
  otherDocuments: OtherDocument[];
  reviewCheckbox: boolean;
  draftPoolId: number | null;

  note: string;
  setNote: (note?: string) => void;
  setDraftPoolId: (id: number | null) => void;

  // UI state
  currentStep: PoolCreationStep;
  showConfirmation: boolean;

  // Validation state
  errors: ValidationErrors;
  submitAttempted: Record<keyof ValidationErrors, boolean>;

  // Submission state
  createPoolError: string | null;

  // Actions - Personal Info
  updatePersonalInfo: (data: Partial<PersonalInfo>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updatePersonalInfoField: (field: keyof PersonalInfo, value: any) => void;
  addPriorName: () => void;
  removePriorName: (index: number) => void;
  updatePriorName: (index: number, field: keyof PriorName, value: string) => void;

  // Actions - Property Info
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updatePropertyInfoField: (field: keyof PropertyInfo, value: any) => void;
  addCoOwner: () => void;
  removeCoOwner: (index: number) => void;
  updateCoOwner: (index: number, field: keyof CoOwner, value: string) => void;
  addPropertyLink: () => void;
  updatePropertyLink: (index: number, value: string) => void;
  removePropertyLink: (index: number) => void;
  addExistingLoan: () => void;
  removeExistingLoan: (index: number) => void;
  updateExistingLoan: (index: number, field: keyof ExistingLoan, value: string) => void;
  calculateUserShare: () => string;

  // Actions - Pool Terms
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updatePoolTermsField: (field: keyof PoolTerms, value: any) => void;
  setTermMonths: (value: string) => void;

  // Actions - Documents
  setDocument: (docType: keyof Omit<Documents, 'propertyPhotos'>, file: UploadedFile | null) => void;
  setPropertyPhotos: (photos: UploadedFile[]) => void;
  removeDocument: (docType: keyof Omit<Documents, 'propertyPhotos'>) => void;
  removePhoto: (index: number) => void;
  addOtherDocument: () => void;
  removeOtherDocument: (index: number) => void;
  updateOtherDocumentTitle: (index: number, title: string) => void;
  setOtherDocumentFile: (index: number, file: UploadedFile | null) => void;
  setOtherDocuments: (docs: OtherDocument[]) => void;

  // Actions - UI
  setCurrentStep: (step: PoolCreationStep) => void;
  setSelectedPoolType: (type: PoolType) => void;
  setShowConfirmation: (show: boolean) => void;
  setReviewCheckbox: (value: boolean) => void;

  // Actions - Validation
  setErrors: (step: keyof ValidationErrors, errors: Record<string, string[]>) => void;
  setSubmitAttempted: (step: keyof ValidationErrors, attempted: boolean) => void;

  // Actions - Submission
  setCreatePoolError: (error: string | null) => void;

  // Actions - Reset
  resetForm: () => void;
};

// Initial states
const initialPersonalInfo: PersonalInfo = {
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  countryCode: DEFAULT_COUNTRY_CODE,
  dateOfBirth: '',
  primaryAddressLine1: '',
  primaryAddressLine2: '',
  primaryCity: '',
  primaryState: 'California',
  primaryZipCode: '',
  primaryCountry: 'United States',
  priorNames: [{ firstName: '', middleName: '', lastName: '' }],
  sameAsPrimaryAddress: false,
  mailingAddressLine1: '',
  mailingAddressLine2: '',
  mailingCity: '',
  mailingState: 'California',
  mailingZipCode: '',
  mailingCountry: 'United States',
};

const initialPropertyInfo: PropertyInfo = {
  propertyAddressLine1: '',
  propertyAddressLine2: '',
  propertyCity: '',
  propertyState: 'California',
  propertyZipCode: '',
  propertyCountry: 'United States',
  propertyOccupancyStatus: null,
  propertyValue: '',
  propertyLinks: [{ url: '' }],
  hasCoOwners: false,
  coOwners: [{ firstName: '', middleName: '', lastName: '', percentage: '' }],
  existingLoans: [{ remainingBalance: '' }],
  sameAsPrimaryAddress: false,
};

const initialPoolTerms: PoolTerms = {
  poolAmount: '',
  roiRate: '',
  loanType: null,
  termMonths: '',
};

const initialDocuments: Documents = {
  governmentId: null,
  appraisalReport: null,
  homeInsurance: null,
  taxReturn: null,
  mortgageStatement: null,
  titleDeed: null,
  coOwnerId: null,
  propertyPhotos: [],
};

const initialOtherDocuments: OtherDocument[] = [];

const initialErrors: ValidationErrors = {
  personalInfo: {},
  propertyInfo: {},
  poolTerms: {},
  documents: {},
  review: {},
};

const initialSubmitAttempted: Record<keyof ValidationErrors, boolean> = {
  personalInfo: false,
  propertyInfo: false,
  poolTerms: false,
  documents: false,
  review: false,
};

export const usePoolFormStore = create<PoolFormState>()(
  devtools(
    (set, get) => ({
      // Initial state
      personalInfo: initialPersonalInfo,
      propertyInfo: initialPropertyInfo,
      poolTerms: initialPoolTerms,
      documents: initialDocuments,
      otherDocuments: initialOtherDocuments,
      currentStep: PoolCreationStep.POOL_TYPE,
      selectedPoolType: PoolType.EQUITY,
      showConfirmation: false,
      reviewCheckbox: false,
      note: '',
      errors: initialErrors,
      submitAttempted: initialSubmitAttempted,
      createPoolError: null,
      draftPoolId: null,
      setDraftPoolId: (id) => set({ draftPoolId: id }),
      // Personal Info Actions
      updatePersonalInfo: (data) =>
        set((state) => ({
          personalInfo: { ...state.personalInfo, ...data },
        })),
      updatePersonalInfoField: (field, value) =>
        set((state) => {
          // Clear error for this specific field when user changes it
          const currentErrors = { ...state.errors.personalInfo };
          delete currentErrors[field];

          if (field === 'sameAsPrimaryAddress') {
            delete currentErrors.mailingAddressLine1;
            delete currentErrors.mailingAddressLine2;
            delete currentErrors.mailingCity;
            delete currentErrors.mailingState;
            delete currentErrors.mailingZipCode;
            delete currentErrors.mailingCountry;
          }

          return {
            personalInfo: { ...state.personalInfo, [field]: value },
            errors: {
              ...state.errors,
              personalInfo: currentErrors,
            },
          };
        }),
      addPriorName: () =>
        set((state) => ({
          personalInfo: {
            ...state.personalInfo,
            priorNames: [...state.personalInfo.priorNames, { firstName: '', middleName: '', lastName: '' }],
          },
        })),
      removePriorName: (index) =>
        set((state) => {
          const newPriorNames = state.personalInfo.priorNames.filter((_, i) => i !== index);
          return {
            personalInfo: {
              ...state.personalInfo,
              priorNames: newPriorNames.length > 0 ? newPriorNames : [{ firstName: '', middleName: '', lastName: '' }],
            },
          };
        }),
      updatePriorName: (index, field, value) =>
        set((state) => {
          const newPriorNames = [...state.personalInfo.priorNames];
          newPriorNames[index] = { ...newPriorNames[index], [field]: value };
          return {
            personalInfo: {
              ...state.personalInfo,
              priorNames: newPriorNames,
            },
          };
        }),

      // Property Info Actions
      updatePropertyInfoField: (field, value) =>
        set((state) => {
          // Clear error for this specific field when user changes it
          const currentErrors = { ...state.errors.propertyInfo };
          delete currentErrors[field];

          if (field === 'sameAsPrimaryAddress') {
            delete currentErrors.propertyAddressLine1;
            delete currentErrors.propertyAddressLine2;
            delete currentErrors.propertyCity;
            delete currentErrors.propertyState;
            delete currentErrors.propertyZipCode;
            delete currentErrors.propertyCountry;
          }

          // clear ltv errors, because it is affected by property value
          const poolTermsErrors = { ...state.errors.poolTerms };
          if (field === 'propertyValue') {
            delete poolTermsErrors['ltv'];
          }

          return {
            propertyInfo: { ...state.propertyInfo, [field]: value },
            errors: {
              ...state.errors,
              propertyInfo: currentErrors,
              poolTerms: poolTermsErrors,
            },
          };
        }),
      addCoOwner: () =>
        set((state) => ({
          propertyInfo: {
            ...state.propertyInfo,
            coOwners: [...state.propertyInfo.coOwners, { firstName: '', middleName: '', lastName: '', percentage: '' }],
          },
        })),
      removeCoOwner: (index) =>
        set((state) => {
          const newCoOwners = state.propertyInfo.coOwners.filter((_, i) => i !== index);
          return {
            propertyInfo: {
              ...state.propertyInfo,
              coOwners:
                newCoOwners.length > 0
                  ? newCoOwners
                  : [{ firstName: '', middleName: '', lastName: '', percentage: '' }],
            },
          };
        }),
      updateCoOwner: (index, field, value) =>
        set((state) => {
          const newCoOwners = [...state.propertyInfo.coOwners];
          newCoOwners[index] = { ...newCoOwners[index], [field]: value };
          return {
            propertyInfo: {
              ...state.propertyInfo,
              coOwners: newCoOwners,
            },
          };
        }),
      addPropertyLink: () =>
        set((state) => {
          const newIndex = state.propertyInfo.propertyLinks.length;
          const currentErrors = { ...state.errors.propertyInfo };
          // Clear error for the new link index and also clear the "no links" error if it exists
          delete currentErrors[`propertyLinks.${newIndex}.url`];
          delete currentErrors['propertyLinks.0.url'];

          return {
            propertyInfo: {
              ...state.propertyInfo,
              propertyLinks: [...state.propertyInfo.propertyLinks, { url: '' }],
            },
            errors: {
              ...state.errors,
              propertyInfo: currentErrors,
            },
          };
        }),
      updatePropertyLink: (index, value) =>
        set((state) => {
          const newLinks = [...state.propertyInfo.propertyLinks];
          newLinks[index] = { url: value.trim() || '' };

          const currentErrors = { ...state.errors.propertyInfo };
          delete currentErrors[`propertyLinks.${index}.url`];
          return {
            propertyInfo: {
              ...state.propertyInfo,
              propertyLinks: newLinks,
            },
            errors: {
              ...state.errors,
              propertyInfo: currentErrors,
            },
          };
        }),
      removePropertyLink: (index) =>
        set((state) => {
          const currentErrors = { ...state.errors.propertyInfo };
          // Clear error for the removed link
          delete currentErrors[`propertyLinks.${index}.url`];

          return {
            propertyInfo: {
              ...state.propertyInfo,
              propertyLinks: state.propertyInfo.propertyLinks.filter((_, i) => i !== index),
            },
            errors: {
              ...state.errors,
              propertyInfo: currentErrors,
            },
          };
        }),
      addExistingLoan: () =>
        set((state) => ({
          propertyInfo: {
            ...state.propertyInfo,
            existingLoans: [...state.propertyInfo.existingLoans, { remainingBalance: '' }],
          },
        })),
      removeExistingLoan: (index) =>
        set((state) => {
          const newLoans = state.propertyInfo.existingLoans.filter((_, i) => i !== index);
          const poolTermsErrors = { ...state.errors.poolTerms };
          delete poolTermsErrors['ltv'];
          return {
            propertyInfo: {
              ...state.propertyInfo,
              existingLoans: newLoans.length > 0 ? newLoans : [{ remainingBalance: '' }],
            },
            errors: {
              ...state.errors,
              poolTerms: poolTermsErrors,
            },
          };
        }),
      updateExistingLoan: (index, field, value) =>
        set((state) => {
          const newLoans = [...state.propertyInfo.existingLoans];
          newLoans[index] = { ...newLoans[index], [field]: value };
          // clear ltv errors, because it is affected by existing loans
          const poolTermsErrors = { ...state.errors.poolTerms };
          delete poolTermsErrors['ltv'];
          return {
            propertyInfo: {
              ...state.propertyInfo,
              existingLoans: newLoans,
            },
            errors: {
              ...state.errors,
              poolTerms: poolTermsErrors,
            },
          };
        }),
      calculateUserShare: () => {
        const state = get();
        return calcUserShare(state.propertyInfo.hasCoOwners, state.propertyInfo.coOwners);
      },

      // Pool Terms Actions
      updatePoolTermsField: (field, value) =>
        set((state) => {
          // Clear error for this specific field when user changes it
          const currentErrors = { ...state.errors.poolTerms };
          delete currentErrors[field];
          //// clear ltv errors to recalculate ltv
          if (field === 'poolAmount') {
            delete currentErrors['ltv'];
          }

          return {
            poolTerms: { ...state.poolTerms, [field]: value },
            errors: {
              ...state.errors,
              poolTerms: currentErrors,
            },
          };
        }),
      setTermMonths: (months) =>
        set((state) => {
          const currentErrors = { ...state.errors.poolTerms };
          delete currentErrors['termMonths'];

          return {
            poolTerms: {
              ...state.poolTerms,
              termMonths: months,
            },
            errors: {
              ...state.errors,
              poolTerms: currentErrors,
            },
          };
        }),

      // Documents Actions
      setDocument: (docType, file) =>
        set((state) => {
          // Clear error for this specific document when user changes it
          const currentErrors = { ...state.errors.documents };
          delete currentErrors[docType];

          return {
            documents: { ...state.documents, [docType]: file },
            errors: {
              ...state.errors,
              documents: currentErrors,
            },
          };
        }),
      setPropertyPhotos: (photos) =>
        set((state) => ({
          documents: { ...state.documents, propertyPhotos: photos },
        })),
      removeDocument: (docType) =>
        set((state) => ({
          documents: { ...state.documents, [docType]: null },
        })),
      removePhoto: (index) =>
        set((state) => ({
          documents: {
            ...state.documents,
            propertyPhotos: state.documents.propertyPhotos.filter((_, i) => i !== index),
          },
        })),
      addOtherDocument: () =>
        set((state) => ({
          otherDocuments: [...state.otherDocuments, { title: '', file: null }],
        })),
      removeOtherDocument: (index) =>
        set((state) => {
          const currentErrors = { ...state.errors.documents };
          delete currentErrors[`otherDocuments.${index}.title`];
          delete currentErrors[`otherDocuments.${index}.file`];

          return {
            otherDocuments: state.otherDocuments.filter((_, i) => i !== index),
            errors: {
              ...state.errors,
              documents: currentErrors,
            },
          };
        }),
      updateOtherDocumentTitle: (index, title) =>
        set((state) => {
          const newOtherDocuments = [...state.otherDocuments];
          newOtherDocuments[index] = { ...newOtherDocuments[index], title };

          const currentErrors = { ...state.errors.documents };
          delete currentErrors[`otherDocuments.${index}.title`];

          return {
            otherDocuments: newOtherDocuments,
            errors: {
              ...state.errors,
              documents: currentErrors,
            },
          };
        }),
      setOtherDocumentFile: (index, file) =>
        set((state) => {
          const newOtherDocuments = [...state.otherDocuments];
          newOtherDocuments[index] = { ...newOtherDocuments[index], file };

          const currentErrors = { ...state.errors.documents };
          delete currentErrors[`otherDocuments.${index}.file`];

          return {
            otherDocuments: newOtherDocuments,
            errors: {
              ...state.errors,
              documents: currentErrors,
            },
          };
        }),
      setOtherDocuments: (docs) =>
        set(() => ({
          otherDocuments: docs,
        })),

      // UI Actions
      setCurrentStep: (step) => set({ currentStep: step }),
      setSelectedPoolType: (type) => set({ selectedPoolType: type }),
      setShowConfirmation: (show) => set({ showConfirmation: show }),
      setReviewCheckbox: (value) => set({ reviewCheckbox: value }),
      setNote: (note = '') => set({ note }),

      // Validation Actions
      setErrors: (step, errors) =>
        set((state) => ({
          errors: { ...state.errors, [step]: errors },
        })),
      setSubmitAttempted: (step, attempted) =>
        set((state) => ({
          submitAttempted: { ...state.submitAttempted, [step]: attempted },
        })),

      // Submission Actions
      setCreatePoolError: (error) => set({ createPoolError: error }),

      // Reset Actions
      resetForm: () =>
        set({
          personalInfo: initialPersonalInfo,
          propertyInfo: initialPropertyInfo,
          poolTerms: initialPoolTerms,
          documents: initialDocuments,
          otherDocuments: initialOtherDocuments,
          currentStep: PoolCreationStep.POOL_TYPE,
          selectedPoolType: PoolType.EQUITY,
          showConfirmation: false,
          reviewCheckbox: false,
          note: '',
          errors: initialErrors,
          submitAttempted: initialSubmitAttempted,
          createPoolError: null,
          draftPoolId: null,
        }),
    }),
    { name: 'PoolFormStore' }
  )
);

// Selectors for better performance (optional, but recommended)
export const usePersonalInfo = () => usePoolFormStore((state) => state.personalInfo);
export const usePropertyInfo = () => usePoolFormStore((state) => state.propertyInfo);
export const usePoolTerms = () => usePoolFormStore((state) => state.poolTerms);
export const useDocuments = () => usePoolFormStore((state) => state.documents);
export const useOtherDocuments = () => usePoolFormStore((state) => state.otherDocuments);
export const useCurrentStep = () => usePoolFormStore((state) => state.currentStep);

export const useIsStepCompleted = (stepKey: keyof ValidationErrors) =>
  usePoolFormStore((state) => state.submitAttempted[stepKey] && Object.keys(state.errors[stepKey]).length === 0);
